import React, { useState } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { ChevronRight } from "lucide-react";
import Thumbnail from "../../common/Thumbnail";
import {
  formatBytes,
  formatTime,
  getColumnValue,
  renderElementContent,
  extractSubItemReason,
} from "./performanceIssueHelpers";

function IssueDescription({ description, className = "mb-4" }) {
  if (!description) return null;
  return (
    <div
      className={`prose prose-sm max-w-none text-foreground diagnostic-description ${className}`}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(marked.parse(description)),
      }}
    />
  );
}

function ClsCulpritsContent({ issue, screenshot, onThumbnailClick }) {
  const tableData = issue.items[0];
  const rows = tableData.items || [];

  return (
    <>
      <IssueDescription description={issue.description} />
      {rows.length > 0 && (
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="min-w-[640px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-2 px-4 font-semibold w-3/4">Element</th>
                  <th className="py-2 px-4 font-semibold text-right w-1/4">
                    Layout shift score
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td className="py-2 px-4 align-top">
                      {renderElementContent(row, screenshot, onThumbnailClick)}
                    </td>
                    <td className="py-2 px-4 align-top text-right">
                      <div>
                        {typeof row.score === "number"
                          ? row.score.toFixed(3)
                          : row.score}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows
                  .filter((row) => row.subItems?.items?.length > 0)
                  .map((row, rowIndex) => {
                    if (!row.subItems?.items?.length) return null;
                    return (
                      <tr
                        key={`unsized-details-${rowIndex}`}
                        className="bg-muted/50"
                      >
                        <td colSpan={2} className="px-4 py-3">
                          <div>
                            <div className="font-medium">Unsized Images</div>
                            {row.subItems.items.map((subItem, subIdx) => (
                              <div key={subIdx} className="mt-3">
                                {subItem.extra?.snippet && (
                                  <div className="font-mono text-xs break-all">
                                    {subItem.extra.snippet}
                                  </div>
                                )}
                                {subItem.extra?.nodeLabel && (
                                  <div className="mt-2">
                                    {subItem.extra.nodeLabel}
                                  </div>
                                )}
                                {subItem.extra?.selector && (
                                  <div className="text-sm text-muted-foreground mt-1">
                                    {subItem.extra.selector}
                                  </div>
                                )}
                                {subItem.extra?.boundingRect && screenshot && (
                                  <div className="mt-3">
                                    <Thumbnail
                                      screenshot={screenshot}
                                      rect={subItem.extra.boundingRect}
                                      onClick={() =>
                                        onThumbnailClick(
                                          subItem.extra.boundingRect,
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function FontDisplayContent({ issue, screenshot, onThumbnailClick, pageUrl }) {
  const headings = issue.headings || [];
  const pageHostname = pageUrl ? new URL(pageUrl).hostname : "";
  const groupedByOrigin = issue.items.reduce((acc, item) => {
    const origin = new URL(item.url).origin;
    if (!acc[origin]) acc[origin] = [];
    acc[origin].push(item);
    return acc;
  }, {});

  return (
    <>
      <IssueDescription description={issue.description} />
      {Object.keys(groupedByOrigin).length > 0 && (
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="min-w-[640px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  {headings.map((heading) => (
                    <th
                      key={heading.key}
                      className={`py-2 px-4 font-semibold ${
                        heading.valueType === "ms" ? "text-right" : ""
                      }`}
                    >
                      {heading.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(groupedByOrigin).map(([origin, items]) => {
                  const originUrl = new URL(origin);
                  const isFirstParty = originUrl.hostname === pageHostname;
                  const totalWastedMs = items.reduce(
                    (total, item) => total + item.wastedMs,
                    0,
                  );
                  return (
                    <React.Fragment key={origin}>
                      <tr className="bg-muted/50">
                        <td className="py-2 px-4">
                          <div className="flex items-center">
                            <span>{originUrl.hostname}</span>
                            {isFirstParty && (
                              <span className="ml-2 text-xs text-brand bg-muted border border-border px-2 py-0.5 rounded-full">
                                1st Party
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-4 text-right">
                          {formatTime(totalWastedMs)}
                        </td>
                      </tr>
                      {items.map((item, index) => (
                        <tr key={index}>
                          {headings.map((heading) => (
                            <td
                              key={heading.key}
                              className={`py-2 px-4 align-top ${
                                heading.valueType === "ms" ? "text-right" : ""
                              } ${heading.key === "url" ? "pl-8" : ""}`}
                            >
                              {getColumnValue(
                                item,
                                heading,
                                screenshot,
                                onThumbnailClick,
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ImageDeliveryContent({
  issue,
  screenshot,
  onThumbnailClick,
  pageUrl,
}) {
  const headings = issue.headings || [];
  const pageHostname = pageUrl ? new URL(pageUrl).hostname : "";

  return (
    <>
      <IssueDescription description={issue.description} />
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <table className="min-w-[640px] w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              {headings.map((heading) => (
                <th
                  key={heading.key}
                  className={`py-2 px-4 font-semibold ${
                    heading.valueType === "bytes" || heading.valueType === "ms"
                      ? "text-right"
                      : ""
                  }`}
                >
                  {heading.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {issue.items.map((item, index) => (
              <React.Fragment key={index}>
                <tr>
                  {headings.map((heading) => {
                    const value = item[heading.key];
                    if (heading.key === "url") {
                      const itemUrl = new URL(value);
                      const isFirstParty = itemUrl.hostname === pageHostname;
                      const pathParts = itemUrl.pathname
                        .split("/")
                        .filter(Boolean);
                      const filename =
                        pathParts[pathParts.length - 1] || itemUrl.pathname;
                      const shortUrl =
                        pathParts.length > 1
                          ? `…/${pathParts.slice(-2).join("/")}`
                          : filename;
                      return (
                        <td
                          key={`${index}-${heading.key}`}
                          className="py-2 px-4 align-top min-w-0 max-w-[280px]"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <a
                              href={value}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand hover:underline truncate min-w-0"
                              title={value}
                            >
                              {shortUrl}
                            </a>
                            {isFirstParty && (
                              <span className="flex-shrink-0 text-xs text-brand bg-muted border border-border px-2 py-0.5 rounded-full whitespace-nowrap">
                                1st Party
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    }
                    const isNodeColumn =
                      heading.itemType === "node" || heading.key === "node";
                    return (
                      <td
                        key={`${index}-${heading.key}`}
                        className={`py-2 px-4 align-top ${
                          heading.valueType === "bytes" ||
                          heading.valueType === "ms"
                            ? "text-right whitespace-nowrap"
                            : ""
                        } ${isNodeColumn ? "w-[88px] sm:w-24" : ""}`}
                      >
                        {getColumnValue(
                          item,
                          heading,
                          screenshot,
                          onThumbnailClick,
                        )}
                      </td>
                    );
                  })}
                </tr>
                {item.subItems?.items?.length > 0 && (() => {
                  const reasons = item.subItems.items
                    .map((subItem) => {
                      const reason = extractSubItemReason(subItem);
                      if (!reason) return null;
                      const save =
                        subItem.wastedBytes != null
                          ? formatBytes(subItem.wastedBytes)
                          : null;
                      return save ? `${reason} (${save})` : reason;
                    })
                    .filter(Boolean);

                  if (!reasons.length) return null;

                  return (
                    <tr key={`${index}-sub-reasons`} className="bg-muted/20">
                      <td
                        colSpan={headings.length}
                        className="py-2 px-4 text-xs text-muted-foreground leading-relaxed"
                      >
                        {reasons.length === 1 ? (
                          reasons[0]
                        ) : (
                          <ul className="list-disc pl-4 space-y-1">
                            {reasons.map((reason, ri) => (
                              <li key={ri}>{reason}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  );
                })()}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DocumentLatencyContent({ issue }) {
  const items = issue.details.items || {};
  const debugData = issue.details.debugData || {};

  const CheckRow = ({ itemKey }) => {
    const row = items[itemKey];
    if (!row) return null;
    const ok = row.value;
    return (
      <div
        className={`flex items-center ${ok ? "text-green-600" : "text-red-600"}`}
      >
        {ok ? (
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
        <span>{row.label}</span>
      </div>
    );
  };

  return (
    <>
      <IssueDescription description={issue.description} />
      <div className="space-y-3">
        <CheckRow itemKey="noRedirects" />
        <CheckRow itemKey="serverResponseIsFast" />
        <CheckRow itemKey="usesCompression" />
        {debugData?.metricSavings && (
          <div className="mt-4 pt-4 border-t border-border">
            <h5 className="font-medium text-foreground mb-2">Metrics Impact</h5>
            <div className="grid grid-cols-2 gap-2">
              {debugData.metricSavings.LCP && (
                <div className="bg-muted p-2 rounded">
                  <span className="font-medium">LCP Impact:</span>{" "}
                  {debugData.metricSavings.LCP} ms
                </div>
              )}
              {debugData.metricSavings.FCP && (
                <div className="bg-muted p-2 rounded">
                  <span className="font-medium">FCP Impact:</span>{" "}
                  {debugData.metricSavings.FCP} ms
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function LcpPhasesContent({ issue, screenshot, onThumbnailClick }) {
  const tableItem = issue.items.find((item) => item.type === "table");
  const nodeItem = issue.items.find((item) => item.type === "node");
  const headings = tableItem?.headings || [];
  const rows = tableItem?.items || [];

  return (
    <>
      <IssueDescription description={issue.description} />
      {rows.length > 0 && (
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                {headings.map((heading) => (
                  <th
                    key={heading.key}
                    className={`py-2 px-4 font-semibold ${
                      heading.valueType === "ms" ? "text-right" : ""
                    }`}
                  >
                    {heading.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((row, index) => (
                <tr key={index}>
                  {headings.map((heading) => (
                    <td
                      key={`${index}-${heading.key}`}
                      className={`py-2 px-4 align-top ${
                        heading.valueType === "ms" ? "text-right" : ""
                      }`}
                    >
                      {heading.valueType === "ms"
                        ? formatTime(row[heading.key])
                        : row[heading.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nodeItem && (
        <div className="mt-4">
          <div className="text-sm text-muted-foreground mb-1">
            <span className="font-medium">Selector:</span> {nodeItem.selector}
          </div>
          {nodeItem.snippet && (
            <div className="mb-2">
              <span className="font-medium">Snippet:</span>
              <code className="ml-2 text-xs bg-muted p-2 rounded">
                {nodeItem.snippet}
              </code>
            </div>
          )}
          {nodeItem.boundingRect &&
            screenshot &&
            nodeItem.boundingRect.width > 0 &&
            nodeItem.boundingRect.height > 0 && (
              <div className="mt-2">
                <Thumbnail
                  screenshot={screenshot}
                  rect={nodeItem.boundingRect}
                  onClick={() => onThumbnailClick(nodeItem.boundingRect)}
                />
              </div>
            )}
        </div>
      )}
    </>
  );
}

function NetworkDependencyTreeContent({ issue }) {
  const formatKB = (bytes) => {
    if (typeof bytes !== "number" || isNaN(bytes)) return "";
    return `${(bytes / 1024).toFixed(2)} KiB`;
  };
  const formatMs = (ms) => {
    if (typeof ms !== "number" || isNaN(ms)) return "";
    return `${Math.round(ms)} ms`;
  };

  const renderChain = (node, depth = 0) => {
    const url = node.url || "";
    const shortUrl = url.length > 60 ? `...${url.slice(-60)}` : url;
    const latency = node.navStartToEndTime;
    const size = node.transferSize;
    const colorClass = node.isLongest
      ? "text-red-600 font-semibold"
      : "text-foreground";

    return (
      <div key={url + depth} style={{ marginLeft: depth * 24 }}>
        <div className={`flex items-center space-x-2 ${colorClass}`}>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs truncate hover:underline whitespace-nowrap"
              title={url}
            >
              {shortUrl}
            </a>
          ) : (
            <span
              className="font-mono text-xs truncate whitespace-nowrap"
              title={url}
            >
              {shortUrl}
            </span>
          )}
          {latency !== undefined && (
            <span className="ml-2 text-xs">- {formatMs(latency)}</span>
          )}
          {size !== undefined && (
            <span className="ml-2 text-xs text-muted-foreground">
              , {formatKB(size)}
            </span>
          )}
        </div>
        {node.children &&
          Object.values(node.children).map((child) =>
            renderChain(child, depth + 1),
          )}
      </div>
    );
  };

  const chains = issue.details.items[0].value.chains;
  const rootNodes = Object.values(chains);
  const maxLatency = issue.details.longestChain?.duration;

  return (
    <>
      <IssueDescription description={issue.description} />
      {maxLatency !== undefined && (
        <div className="mb-2 text-sm text-foreground">
          <span className="font-medium">Maximum critical path latency:</span>{" "}
          {formatMs(maxLatency)}
        </div>
      )}
      <div className="mt-2">
        <div className="font-medium text-foreground mb-1">
          Initial Navigation
        </div>
        <div className="overflow-x-auto w-full">
          <div className="min-w-[700px] md:min-w-[900px]">
            {rootNodes.map((node) => renderChain(node))}
          </div>
        </div>
      </div>
    </>
  );
}

function ThirdPartiesContent({ issue }) {
  const [entityOpen, setEntityOpen] = useState({});

  const getTag = (name) => {
    if (!name) return null;
    const tags = [
      { tag: "Tag-Manager", match: /tag.?manager/i },
      { tag: "Social", match: /facebook|twitter|social/i },
      { tag: "Cdn", match: /cdn/i },
      { tag: "Marketing", match: /sojern|marketing/i },
      {
        tag: "Ad",
        match: /ad|doubleclick|pubmatic|appnexus|improve digital|rubicon/i,
      },
    ];
    return tags.find((t) => t.match.test(name))?.tag ?? null;
  };

  const renderUrl = (url) => {
    try {
      const u = new URL(url);
      const path =
        u.pathname.length > 40 ? "..." + u.pathname.slice(-40) : u.pathname;
      return (
        <span>
          <span className="text-muted-foreground">{u.hostname}</span>
          <span className="text-foreground"> {path}</span>
        </span>
      );
    } catch {
      return url;
    }
  };

  return (
    <>
      <IssueDescription description={issue.description} />
      <div className="overflow-x-auto">
        <table className="min-w-[700px] text-sm border">
          <thead>
            <tr className="bg-muted">
              <th className="py-2 px-4 text-left whitespace-nowrap">
                3rd party
              </th>
              <th className="py-2 px-4 text-right whitespace-nowrap">
                Transfer size
              </th>
              <th className="py-2 px-4 text-right whitespace-nowrap">
                Main thread time
              </th>
            </tr>
          </thead>
          <tbody>
            {issue.details.items.map((entity, idx) => (
              <React.Fragment key={entity.entity || idx}>
                <tr
                  className="bg-muted/50 cursor-pointer font-semibold"
                  onClick={() =>
                    setEntityOpen((o) => ({ ...o, [idx]: !o[idx] }))
                  }
                >
                  <td
                    className="py-2 px-4 flex items-center whitespace-nowrap overflow-hidden text-ellipsis"
                    title={entity.entity}
                  >
                    <ChevronRight
                      size={16}
                      className={`mr-2 text-muted-foreground transition-transform ${
                        entityOpen[idx] ? "rotate-90" : ""
                      }`}
                    />
                    {entity.entity}
                    {getTag(entity.entity) && (
                      <span className="ml-2 text-xs text-brand bg-muted border border-border px-2 py-0.5 rounded-full">
                        {getTag(entity.entity)}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-4 text-right whitespace-nowrap">
                    {formatBytes(entity.transferSize)}
                  </td>
                  <td className="py-2 px-4 text-right whitespace-nowrap">
                    {formatTime(entity.mainThreadTime)}
                  </td>
                </tr>
                {entityOpen[idx] &&
                  entity.subItems?.items?.map((item, subIdx) => (
                    <tr key={item.url || subIdx} className="border-t bg-muted">
                      <td className="py-2 px-8 text-xs break-all">
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand underline"
                          title={item.url}
                        >
                          {renderUrl(item.url)}
                        </a>
                      </td>
                      <td className="py-2 px-4 text-right whitespace-nowrap">
                        {formatBytes(item.transferSize)}
                      </td>
                      <td className="py-2 px-4 text-right whitespace-nowrap">
                        {formatTime(item.mainThreadTime)}
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <div className="text-xs text-muted-foreground mt-2">
          Click a row to expand/collapse details for each provider.
        </div>
      </div>
    </>
  );
}

function DefaultIssueContent({ issue, screenshot, onThumbnailClick }) {
  const headings = issue.headings || [
    { key: "node", itemType: "node", label: "Failing Elements" },
  ];

  return (
    <>
      {issue.description && (!issue.items || issue.items.length === 0) && (
        <IssueDescription description={issue.description} className="" />
      )}
      {issue.items?.length > 0 && (
        <>
          <IssueDescription description={issue.description} />
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  {headings.map((heading) => (
                    <th
                      key={heading.key}
                      className={`py-2 px-4 font-semibold ${
                        heading.itemType === "bytes" ||
                        heading.itemType === "ms"
                          ? "text-right"
                          : ""
                      }`}
                    >
                      {heading.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {issue.items.map((item, index) => (
                  <tr key={index}>
                    {headings.map((heading) => (
                      <td
                        key={`${index}-${heading.key}`}
                        className={`py-2 px-4 align-top ${
                          heading.itemType === "bytes" ||
                          heading.itemType === "ms"
                            ? "text-right"
                            : ""
                        }`}
                      >
                        {getColumnValue(
                          item,
                          heading,
                          screenshot,
                          onThumbnailClick,
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function renderIssueBody(issueKey, issue, props) {
  const { screenshot, onThumbnailClick, pageUrl } = props;

  if (issueKey === "clsCulpritsInsight" && issue.items?.length > 0) {
    return (
      <ClsCulpritsContent
        issue={issue}
        screenshot={screenshot}
        onThumbnailClick={onThumbnailClick}
      />
    );
  }

  if (issueKey === "fontDisplayInsight" && issue.items?.length > 0) {
    return (
      <FontDisplayContent
        issue={issue}
        screenshot={screenshot}
        onThumbnailClick={onThumbnailClick}
        pageUrl={pageUrl}
      />
    );
  }

  if (issueKey === "imageDeliveryInsight" && issue.items?.length > 0) {
    return (
      <ImageDeliveryContent
        issue={issue}
        screenshot={screenshot}
        onThumbnailClick={onThumbnailClick}
        pageUrl={pageUrl}
      />
    );
  }

  if (issueKey === "documentLatencyInsight" && issue.details) {
    return <DocumentLatencyContent issue={issue} />;
  }

  if (issueKey === "lcpPhasesInsight" && issue.items?.length > 0) {
    return (
      <LcpPhasesContent
        issue={issue}
        screenshot={screenshot}
        onThumbnailClick={onThumbnailClick}
      />
    );
  }

  if (
    issueKey === "networkDependencyTreeInsight" &&
    issue.details?.items?.[0]?.value?.chains
  ) {
    return <NetworkDependencyTreeContent issue={issue} />;
  }

  if (issueKey === "thirdPartiesInsight" && issue.details?.items?.length > 0) {
    return <ThirdPartiesContent issue={issue} />;
  }

  return (
    <DefaultIssueContent
      issue={issue}
      screenshot={screenshot}
      onThumbnailClick={onThumbnailClick}
    />
  );
}

export default function PerformanceIssueContent({
  issueKey,
  issue,
  screenshot,
  pageUrl,
  onThumbnailClick,
  embedded = false,
}) {
  const body = renderIssueBody(issueKey, issue, {
    screenshot,
    onThumbnailClick,
    pageUrl,
  });

  if (!body) return null;

  return <div className={embedded ? "pt-3" : "p-4 border-t"}>{body}</div>;
}
